import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VictoryAxisProps } from 'victory';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryClipContainer,
  VictoryLine,
  VictoryScatter,
  VictoryVoronoiContainer,
} from 'victory';

import { AreaGradient } from './components/AreaGradient';
import { DataLabel } from './components/DataLabel/DataLabel';
import { VictoryWmgTheme } from './components/VictoryWmgTheme';

export type LineGraphData = {
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}[];

export type StreamType =
  | 'streams'
  | 'organicStreams'
  | 'programmedStreams'
  | 'benchmarkStreams';

interface LineGraphProps {
  streamType?: StreamType;
  data: LineGraphData[];
  lineColors?: string[];
  containerClassName?: string;
  xAxisProps?: VictoryAxisProps;
  yAxisProps?: VictoryAxisProps;
  tooltip?: ReactElement | undefined;
  tooltipOffset?: number[];
  targetPercentage?: number;
  dataLabelText?: string;
}

// Fixed chart height - no scaling
const CHART_HEIGHT = 400;

interface HoverState {
  index: number;
  cursorX: number;
}

function LineGraph({
  data,
  streamType,
  targetPercentage = 100,
  containerClassName = '',
  lineColors = ['#000000'],
  xAxisProps = {},
  yAxisProps = {},
  tooltip = undefined,
  tooltipOffset = [0, 0],
  dataLabelText = 'Label',
}: LineGraphProps) {
  const [hoverState, setHoverState] = useState<HoverState | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(400);
  const containerRef = useRef<HTMLDivElement>(null);
  const areaGradientIdPrefix = 'areaGradient_';

  const isBenchmarkStream = streamType === 'benchmarkStreams';

  // Track container width for responsive chart (like MultiLineChart)
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // Chart padding - larger to accommodate axis labels
  const chartPadding = useMemo(() => {
    return {
      top: 20,
      left: yAxisProps?.orientation === 'left' ? 50 : 10,
      right: 20,
      bottom: 60,
    };
  }, [yAxisProps?.orientation]);

  // Handle mouse move to track cursor position (like MultiLineChart)
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = containerRef.current;

      if (!container || !data[0]?.length) {
        return;
      }

      const svg = container.querySelector('svg');

      if (!svg) {
        return;
      }

      // Use SVG coordinate transformation for accurate positioning
      const point = svg.createSVGPoint();

      point.x = e.clientX;
      point.y = e.clientY;

      const ctm = svg.getScreenCTM();

      if (!ctm) {
        return;
      }

      const svgPoint = point.matrixTransform(ctm.inverse());
      const svgX = svgPoint.x;

      const chartAreaLeft = chartPadding.left;
      const chartAreaRight = containerWidth - chartPadding.right;
      const chartAreaWidth = chartAreaRight - chartAreaLeft;

      if (svgX < chartAreaLeft || svgX > chartAreaRight) {
        setHoverState(null);

        return;
      }

      const containerRect = container.getBoundingClientRect();
      const cursorX = e.clientX - containerRect.left;

      const relativeX = svgX - chartAreaLeft;
      const dataLength = data[0].length;
      const index = Math.round((relativeX / chartAreaWidth) * (dataLength - 1));
      const clampedIndex = Math.max(0, Math.min(dataLength - 1, index));

      setHoverState({ index: clampedIndex, cursorX });
    },
    [data, chartPadding.left, chartPadding.right, containerWidth],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverState(null);
  }, []);

  // Format raw value for tooltip display
  const formatRawValue = (rawValue: number | undefined): string => {
    if (typeof rawValue !== 'number') {
      return 'N/A';
    }

    if (rawValue >= 1000) {
      return `${(rawValue / 1000).toFixed(1)}K`;
    }

    return String(rawValue);
  };

  // Get hovered data for tooltip
  const hoveredData =
    hoverState !== null
      ? {
          date: data[0]?.[hoverState.index]?.date || '',
          values: data.map((series, idx) => ({
            seriesName: (series[hoverState.index] as { seriesName?: string })
              ?.seriesName,
            rawValue: (series[hoverState.index] as { rawValue?: number })
              ?.rawValue,
            y: series[hoverState.index]?.y,
            color: lineColors[idx],
          })),
        }
      : null;

  return (
    <div
      ref={containerRef}
      className={`${containerClassName} relative cursor-crosshair`}
      style={{ height: CHART_HEIGHT }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {lineColors.map((color, index) => (
        <AreaGradient
          key={index}
          id={areaGradientIdPrefix + index}
          lineColor={color}
        />
      ))}

      <VictoryChart
        width={containerWidth}
        height={CHART_HEIGHT}
        padding={chartPadding}
        domainPadding={[0, 0]}
        domain={{ y: [-5, 100] }}
        theme={VictoryWmgTheme}
        containerComponent={
          isBenchmarkStream ? undefined : <VictoryVoronoiContainer />
        }
      >
        {isBenchmarkStream ? (
          <DataLabel
            x={1}
            y={data[0][data[0].length - 1].y}
            text={[dataLabelText, `${targetPercentage}%`]}
          />
        ) : null}

        <VictoryAxis crossAxis {...xAxisProps} />
        <VictoryAxis dependentAxis {...yAxisProps} />

        <VictoryAxis
          orientation="top"
          style={{
            grid: { stroke: 'transparent' },
            ticks: { size: 0 },
            tickLabels: { fill: 'transparent' },
          }}
        />

        {/* Right Border */}
        <VictoryAxis
          dependentAxis
          orientation="right"
          style={{
            ticks: { size: 0 },
            tickLabels: { fill: 'transparent' },
          }}
        />

        {data.map((dataPoint, index) => (
          <VictoryArea
            groupComponent={
              <VictoryClipContainer clipPadding={{ top: 10, right: 10 }} />
            }
            key={index}
            interpolation="monotoneX"
            data={dataPoint}
            style={{
              data: {
                stroke: lineColors[index],
                strokeWidth: 2.5,
                fill: `url(#${areaGradientIdPrefix + index})`,
                fillOpacity: 0,
                strokeDasharray:
                  isBenchmarkStream && index === 1 ? '4' : 'none',
              },
            }}
          />
        ))}

        {/* Vertical cursor line */}
        {hoverState !== null && data[0]?.[hoverState.index] ? (
          <VictoryLine
            data={[
              { x: data[0][hoverState.index].x, y: -5 },
              { x: data[0][hoverState.index].x, y: 100 },
            ]}
            style={{
              data: {
                stroke: 'rgba(255, 255, 255, 0.3)',
                strokeWidth: 1,
                strokeDasharray: '4,2',
              },
            }}
          />
        ) : null}

        {/* Hover dots on all series */}
        {hoverState !== null &&
          data.map((series, idx) => {
            const point = series[hoverState.index];

            if (!point) {
              return null;
            }

            return (
              <VictoryScatter
                key={`hover-dot-${idx}`}
                data={[{ x: point.x, y: point.y }]}
                size={5}
                style={{
                  data: {
                    fill: lineColors[idx],
                    stroke: '#1a1a1a',
                    strokeWidth: 2,
                  },
                }}
              />
            );
          })}
      </VictoryChart>

      {/* Tooltip outside SVG - styled like MultiLineChart */}
      {/* Tooltip outside SVG - styled like MultiLineChart */}
      {hoverState !== null && hoveredData !== null && tooltip !== undefined ? (
        <div
          className="pointer-events-none absolute top-[20px] z-10 rounded-md border px-lg py-md shadow-lg bg-level-03 border-emphasis-subtle"
          style={
            hoverState.index > data[0].length / 2
              ? { right: `calc(100% - ${hoverState.cursorX}px + 12px)` }
              : { left: hoverState.cursorX + 12 + (tooltipOffset[0] || 0) }
          }
        >
          <div className="mb-sm border-b pb-sm text-[13px] font-semibold border-emphasis-subtle text-default">
            {hoveredData.date}
          </div>
          <div className="flex flex-col gap-[5px]">
            {hoveredData.values.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between gap-xl text-[13px]"
              >
                <div className="flex items-center gap-sm">
                  <div
                    className="h-[8px] w-[8px] rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-subtle">{item.seriesName}</span>
                </div>
                <span className="font-medium tabular-nums text-default">
                  {Number(item.y).toFixed(1)}%
                  <span className="ml-xs text-subtle">
                    ({formatRawValue(item.rawValue)})
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

LineGraph.displayName = 'LineGraph';
export { LineGraph };
